import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
// font-awesome
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import AppSnackbar from "layouts/notifications/Snackbar";
import getLangauges from "services/languageService";
import { addUser } from "services/userService";
import { useTranslation } from "hooks/useTranslation";
import { getUserInfo } from "services/authService";
import { normalizeSenegalPhoneInput, validateSenegalPhoneNineDigits } from "utils/senegalPhone";
import {
  translateApiMessage,
  isEmailAlreadyExistsError,
  isPhoneInvalidFormatError,
} from "utils/translateApiMessage";
import useHierarchySelects from "hooks/useHierarchySelects";
import HierarchySelects from "components/HierarchySelects";

// Helper validation function with translation support
function validateForm(form, t) {
  const errors = {};

  if (!form.first_name)
    errors.first_name = t
      ? t("admin.annotators.modals.validation.firstNameRequired", "First name is required")
      : "First name is required";
  if (!form.last_name)
    errors.last_name = t
      ? t("admin.annotators.modals.validation.lastNameRequired", "Last name is required")
      : "Last name is required";

  if (!form.phoneNumber) {
    errors.phoneNumber = t
      ? t("admin.annotators.modals.validation.phoneRequired", "Phone number is required")
      : "Phone number is required";
  } else {
    const pe = validateSenegalPhoneNineDigits(form.phoneNumber, t);
    if (pe) errors.phoneNumber = pe;
  }

  // Email Validation
  if (!form.email_id) {
    errors.email_id = t
      ? t("admin.annotators.modals.validation.emailRequired", "Email is required")
      : "Email is required";
  } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email_id)) {
    errors.email_id = t
      ? t("admin.annotators.modals.validation.emailInvalid", "Email is invalid")
      : "Email is invalid";
  }

  if (!form.password)
    errors.password = t
      ? t("admin.annotators.modals.validation.passwordRequired", "Password is required")
      : "Password is required";
  if (!form.confirmPassword)
    errors.confirmPassword = t
      ? t(
          "admin.annotators.modals.validation.confirmPasswordRequired",
          "Confirm Password is required"
        )
      : "Confirm Password is required";
  if (form.password && form.confirmPassword && form.password !== form.confirmPassword) {
    errors.confirmPassword = t
      ? t("admin.annotators.modals.validation.passwordsDontMatch", "Passwords do not match")
      : "Passwords do not match";
  }

  if (!form.country)
    errors.country = t
      ? t("admin.annotators.modals.validation.countryRequired", "Country is required")
      : "Country is required";
  if (!form.district)
    errors.district = t
      ? t("admin.annotators.modals.validation.iefRequired", "IEF is required")
      : "IEF is required";
  if (!form.language)
    errors.language = t
      ? t("admin.annotators.modals.validation.languageRequired", "Language is required")
      : "Language is required";
  if (!form.program)
    errors.program = t
      ? t("admin.annotators.modals.validation.programRequired", "Program name is required")
      : "Program name is required";

  return errors;
}

/** Prefer French default when present; otherwise first row from tbl_language. */
function defaultAnnotationLanguage(langRows) {
  const names = (langRows || []).map((r) => r?.language).filter(Boolean);
  if (names.includes("Français")) return "Français";
  if (names.includes("French")) return "French";
  return names[0] || "";
}

export default function AddAnnotator({ open, onClose, onSuccess, title }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phoneNumber: "",
    email_id: "",
    password: "",
    confirmPassword: "",
    country: "Sénégal",
    state: "",
    district: "",
    block: "",
    language: "Français",
    program: "Readnet Sénégal",
  });

  //fetching logged user info
  const user = getUserInfo();

  const [errors, setErrors] = useState({});
  const [languages, setLanguages] = useState([]);
  const [openDropdown, setOpenDropdown] = useState({});

  const hierarchy = useHierarchySelects({ open });

  // Toast-snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "",
    message: "",
  });

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
      type: "",
      message: "",
    }));
  };

  // Reset form on modal open; load languages from API (tbl_language)
  useEffect(() => {
    if (!open) return;

    setErrors({});
    setForm({
      first_name: "",
      last_name: "",
      phoneNumber: "",
      email_id: "",
      password: "",
      confirmPassword: "",
      country: "Sénégal",
      state: "",
      district: "",
      block: "",
      language: "Français",
      program: "Readnet Sénégal",
    });

    const load = async () => {
      try {
        const langRows = await getLangauges();
        const langList = Array.isArray(langRows) ? langRows : [];
        setLanguages(langList);
        const langDefault = defaultAnnotationLanguage(langList);
        setForm((prev) => ({ ...prev, language: langDefault }));
      } catch (error) {
        console.error("Failed to fetch dropdown data", error);
        setLanguages([]);
        setForm((prev) => ({ ...prev, language: "" }));
        showSnackbar(
          "error",
          t
            ? t("admin.annotators.modals.errors.loadDropdowns", "Failed to load dropdown data")
            : "Failed to load dropdown data"
        );
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset form only when modal opens, not when `t` updates
  }, [open]);

  useEffect(() => {
    if (hierarchy.departmentName || hierarchy.iefName) {
      setForm((prev) => ({
        ...prev,
        state: hierarchy.departmentName,
        district: hierarchy.iefName,
      }));
    }
  }, [hierarchy.departmentName, hierarchy.iefName]);

  /* handlers of form fields */
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleHierarchyIefChange = (iefId) => {
    hierarchy.handleIefChange(iefId);
    if (errors.district) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.district;
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const ok = await saveAnnotator(form);
    if (ok) onSuccess?.();
  };

  const handleCancel = () => {
    setForm({
      first_name: "",
      last_name: "",
      phoneNumber: "",
      email_id: "",
      password: "",
      confirmPassword: "",
      country: "Sénégal",
      state: "",
      district: "",
      block: "",
      language: defaultAnnotationLanguage(languages),
      program: "Readnet Sénégal",
    });
    setErrors({});
    onClose();
  };

  const handleNumericChange = (field) => (e) => {
    const value = e.target.value;
    // Allow only digits (or empty string)
    if (/^\d*$/.test(value)) {
      setForm({ ...form, [field]: value });
    }
  };

  const handlePhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "");
    if (digits.length > 12) digits = digits.slice(0, 12);
    setForm({ ...form, phoneNumber: digits });
    if (errors.phoneNumber) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phoneNumber;
        return next;
      });
    }
  };

  const handlePhoneBlur = () => {
    const normalized = normalizeSenegalPhoneInput(form.phoneNumber);
    if (normalized !== form.phoneNumber) {
      setForm((prev) => ({ ...prev, phoneNumber: normalized }));
    }
  };

  const renderSelectField = (
    label,
    field,
    field_value,
    options,
    handler,
    option_value,
    option_label
  ) => (
    <FormControl fullWidth error={Boolean(errors[field])}>
      <InputLabel id={`${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`${field}-label`}
        value={field_value}
        label={label}
        onChange={handler ? handler(field) : handleChange(field)}
        sx={{
          height: 50,
          fontSize: "1rem",
          "& svg": {
            top: "calc(50% - 0.5em)",
            right: 10,
            position: "absolute",
            pointerEvents: "none",
            color: "gray",
          },
        }}
        onOpen={() => setOpenDropdown((prev) => ({ ...prev, [field]: true }))}
        onClose={() => setOpenDropdown((prev) => ({ ...prev, [field]: false }))}
        IconComponent={() => (
          <FontAwesomeIcon icon={openDropdown[field] ? faChevronUp : faChevronDown} />
        )}
      >
        {option_value !== "" && option_label !== ""
          ? options?.map((option) => (
              <MenuItem key={option[option_value]} value={option[option_value]}>
                {option[option_label]}
              </MenuItem>
            ))
          : options?.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
      </Select>
      {errors[field] && (
        <p style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>{errors[field]}</p>
      )}
    </FormControl>
  );

  const renderTextField = (label, field, field_value, type = "text", disabled = false) => (
    <TextField
      label={label}
      type={type}
      fullWidth
      value={field_value}
      onChange={handleChange(field)}
      error={Boolean(errors[field])}
      helperText={errors[field]}
      variant="outlined"
      sx={{ fontSize: "1rem" }}
      disabled={disabled}
    />
  );

  /* save annotator api call */
  async function saveAnnotator(form) {
    let annotatorObj = {
      role: "Annotator",
      country: form.country,
      state: form.state,
      district: form.district,
      block: form.block,
      program_id: form.program,
      language: form.language,
      first_name: form.first_name,
      last_name: form.last_name,
      email_id: form.email_id,
      password: form.password,
      phone_number: normalizeSenegalPhoneInput(form.phoneNumber),
      created_by: user.id,
    };

    try {
      const result = await addUser(annotatorObj);
      if (result.status === "success") {
        showSnackbar(
          "success",
          t("admin.annotators.modals.success.annotatorAdded", "Annotator added successfully")
        );
        onClose();
        return true;
      }

      const msg = translateApiMessage(
        result.message,
        result.code,
        t,
        t("admin.annotators.modals.errors.addAnnotator", "Failed to add annotator")
      );
      if (isEmailAlreadyExistsError(result)) {
        setErrors((prev) => ({ ...prev, email_id: msg }));
      } else if (isPhoneInvalidFormatError(result)) {
        setErrors((prev) => ({ ...prev, phoneNumber: msg }));
      }
      showSnackbar("error", msg);
      return false;
    } catch (error) {
      console.error("Failed to add annotator:", error);
      showSnackbar(
        "error",
        t("admin.annotators.modals.errors.addAnnotator", "Failed to add annotator")
      );
      return false;
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <MDBox p={3}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">
              {title ||
                (t
                  ? t("admin.annotators.modals.addAnnotator", "Add Annotator Details")
                  : "Add Annotator Details")}
            </MDTypography>
            <IconButton onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.firstName", "First Name") : "First Name",
                "first_name",
                form.first_name
              )}
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.lastName", "Last Name") : "Last Name",
                "last_name",
                form.last_name
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={
                  t
                    ? t("admin.annotators.modals.fields.phoneNumber", "Phone Number")
                    : "Phone Number"
                }
                fullWidth
                type="text"
                value={form.phoneNumber}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber}
                variant="outlined"
                placeholder="77XXXXXXX"
                inputProps={{ maxLength: 12, inputMode: "numeric", pattern: "[0-9]*" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ fontSize: "0.875rem", color: "#666" }}>+221</span>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& input": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.email", "Email") : "Email",
                "email_id",
                form.email_id
              )}
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.country", "Country") : "Country",
                "country",
                form.country,
                "",
                true
              )}
            </Grid>
            <Grid item xs={12}>
              <HierarchySelects
                regions={hierarchy.regions}
                departments={hierarchy.departments}
                ias={hierarchy.ias}
                iefs={hierarchy.iefs}
                selectedRegionId={hierarchy.selectedRegionId}
                selectedDepartmentId={hierarchy.selectedDepartmentId}
                selectedIaId={hierarchy.selectedIaId}
                selectedIefId={hierarchy.selectedIefId}
                onRegionChange={hierarchy.handleRegionChange}
                onDepartmentChange={hierarchy.handleDepartmentChange}
                onIaChange={hierarchy.handleIaChange}
                onIefChange={handleHierarchyIefChange}
                disabled={hierarchy.loading}
                districtError={errors.district}
              />
            </Grid>
            <Grid item xs={6}>
              {languages.length === 0 ? (
                <TextField
                  label={t ? t("admin.annotators.modals.fields.language", "Language") : "Language"}
                  fullWidth
                  value={form.language}
                  helperText={
                    t
                      ? t(
                          "admin.annotators.modals.hints.noLanguagesInDb",
                          "No languages in the database yet. Add samples or languages first."
                        )
                      : "No languages in the database yet."
                  }
                  disabled
                  variant="outlined"
                />
              ) : (
                renderSelectField(
                  t ? t("admin.annotators.modals.fields.language", "Language") : "Language",
                  "language",
                  form.language,
                  languages,
                  null,
                  "language",
                  "language"
                )
              )}
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.program", "Program") : "Program",
                "program",
                form.program,
                "",
                true
              )}
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t ? t("admin.annotators.modals.fields.password", "Password") : "Password",
                "password",
                form.password,
                "password"
              )}
            </Grid>
            <Grid item xs={6}>
              {renderTextField(
                t
                  ? t("admin.annotators.modals.fields.confirmPassword", "Confirm Password")
                  : "Confirm Password",
                "confirmPassword",
                form.confirmPassword,
                "password"
              )}
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "2px solid #218838",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#218838",
                    borderColor: "#1e7e34",
                  },
                  "&:active": {
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)",
                  },
                }}
              >
                {t ? t("admin.annotators.modals.buttons.save", "Save") : "Save"}
              </Button>
              <Button
                onClick={handleCancel}
                variant="contained"
                sx={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "2px solid #5a6268",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: "#5a6268",
                    borderColor: "#545b62",
                  },
                }}
              >
                {t ? t("admin.annotators.modals.buttons.cancel", "Cancel") : "Cancel"}
              </Button>
            </Grid>
          </Grid>
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

AddAnnotator.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  title: PropTypes.string,
};

AddAnnotator.defaultProps = {
  title: null,
};
